// src/theme/components/tag.js
import { tagAnatomy } from "@chakra-ui/anatomy";
import { createMultiStyleConfigHelpers } from "@chakra-ui/react";
import { mode, transparentize } from "@chakra-ui/theme-tools";

const { definePartsStyle, defineMultiStyleConfig } =
  createMultiStyleConfigHelpers(tagAnatomy.keys);

const baseStyle = definePartsStyle({
  container: {
    borderRadius: "full",
    fontWeight: "semibold",
    lineHeight: "shorter",
    textTransform: "none",
    display: "inline-flex",
    alignItems: "center",
    gap: 1,
    transitionProperty: "common",
    transitionDuration: "var(--motion-duration-fast)",
    transitionTimingFunction: "var(--motion-ease-apple)",
  },
  label: {
    paddingInlineStart: 0,
    paddingInlineEnd: 0,
  },
  closeButton: {
    borderRadius: "full",
    _focusVisible: {
      boxShadow: "var(--chakra-shadows-focusOutlineAccent)",
      outline: "none",
    },
  },
});

const sizes = {
  xs: definePartsStyle({
    container: { px: 2, py: 0.5, fontSize: "xs" },
  }),
  sm: definePartsStyle({
    container: { px: 2.5, py: 0.5, fontSize: "xs" },
  }),
  md: definePartsStyle({
    container: { px: 3, py: 1, fontSize: "sm" },
  }),
  lg: definePartsStyle({
    container: { px: 4, py: 1.5, fontSize: "md" },
  }),
};

const subtleVariant = definePartsStyle((props) => {
  const { colorScheme: c, theme } = props;
  const bg = mode(`${c}.50`, transparentize(`${c}.400`, 0.24)(theme))(props);
  const color = mode(`${c}.700`, `${c}.200`)(props);
  const borderColor = mode(`${c}.200`, transparentize(`${c}.400`, 0.5)(theme))(props);

  return {
    container: {
      bg,
      color,
      borderWidth: "1px",
      borderColor,
    },
    closeButton: {
      color,
    },
  };
});

const solidVariant = definePartsStyle((props) => {
  const { colorScheme: c } = props;
  const bg = mode(`${c}.500`, `${c}.300`)(props);
  const lightColor = ["yellow", "cyan", "gray"].includes(c) ? "gray.800" : "white";
  const darkColor = c === "gray" ? "whiteAlpha.900" : "gray.900";
  const color = mode(lightColor, darkColor)(props);

  return {
    container: {
      bg,
      color,
      boxShadow: "sm",
    },
    closeButton: {
      color,
    },
  };
});

const outlineVariant = definePartsStyle((props) => {
  const { colorScheme: c } = props;
  const color = mode(`${c}.600`, `${c}.200`)(props);
  const borderColor = mode(`${c}.200`, `${c}.400`)(props);

  return {
    container: {
      bg: "transparent",
      color,
      borderWidth: "1px",
      borderColor,
    },
    closeButton: {
      color,
    },
  };
});

// ====================================================================
// Faz 2 · Sinav statu (exam) tonlu tag varyantlari.
// ====================================================================
const makeStatusTag = (statusPrefix) => definePartsStyle(() => ({
  container: {
    bg: `${statusPrefix}.bg`,
    color: `${statusPrefix}.text`,
    borderWidth: '1px',
    borderColor: `${statusPrefix}.border`,
  },
  closeButton: {
    color: `${statusPrefix}.text`,
  },
}));

const correctTag    = makeStatusTag('exam.correct');
const incorrectTag  = makeStatusTag('exam.incorrect');
const reviewTag     = makeStatusTag('exam.review');
const skippedTag    = makeStatusTag('exam.skipped');
const partialTag    = makeStatusTag('exam.partial');
const srsNewTag     = makeStatusTag('srs.new');
const srsReviewTag  = makeStatusTag('srs.review');
const srsMasteredTag = makeStatusTag('srs.mastered');

export const tagTheme = defineMultiStyleConfig({
  baseStyle,
  sizes,
  variants: {
    subtle: subtleVariant,
    solid: solidVariant,
    outline: outlineVariant,
    // Faz 2 · Sinav statu varyantlari
    correct: correctTag,
    incorrect: incorrectTag,
    review: reviewTag,
    skipped: skippedTag,
    partial: partialTag,
    // Faz 2 · SRS varyantlari
    srsNew: srsNewTag,
    srsReview: srsReviewTag,
    srsMastered: srsMasteredTag,
  },
  defaultProps: {
    variant: "subtle",
    colorScheme: "gray",
    size: "md",
  },
});

export default tagTheme;
